import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import { Modal, Popover } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { isNonEmptyQuoteLocations, parseRefsIndex } from './utils'
import { getLenderInfo } from './constants'
import { ICON } from "./components/GuidelineSelect/GuidelineCheckbox";
import './Chat.css'

const MarkdownRenderer = ({ answer, pageNumber, quoteLocations, referenceFunc, isGenerating = false, isMobile }) => {
    // State to hold lender info
    const [lenderInfo, setLenderInfo] = useState({});
   const firstReference = useRef(false);
    
    // Load lender info on mount
    useEffect(() => {
        getLenderInfo().then(data => {
            setLenderInfo(data);
        }).catch(error => {
            console.error('Failed to load lender info:', error);
        });
    }, []);
    
    // Synchronous version of findPdfByKeywords using loaded lender info
    const findPdfByKeywordsSync = (fileName, lenderData) => {
        // Agency Guidelines mapping - these have their own PDF files
        const agencyGuidelinesMappings = [
            { pattern: /^FHA\s*(Guidelines?|Handbook)?$/i, path: '/scenario_pdfs/FHA-Guidelines.pdf' },
            { pattern: /^VA\s*(Guidelines?|Handbook|Lender'?s?\s*Handbook)?$/i, path: '/scenario_pdfs/VA-Guidelines.pdf' },
            { pattern: /^USDA\s*(Guidelines?|Loan)?$/i, path: '/scenario_pdfs/USDA-Guidelines.pdf' },
            { pattern: /^Fannie\s*Mae\s*(Selling\s*Guide|Guidelines?)?$/i, path: '/scenario_pdfs/Fannie-Mae-Guidelines.pdf' },
            { pattern: /^Freddie\s*Mac\s*(Seller|Guidelines?)?$/i, path: '/scenario_pdfs/Freddie-Mac-Guidelines.pdf' },
        ]
        
        // Remove .pdf suffix and page number suffix for checking 
        // (e.g., "FHA Guidelines.pdf" -> "FHA Guidelines", "FHA Guidelines, p.472" -> "FHA Guidelines")
        const fileNameForCheck = fileName
            .replace(/\.pdf$/i, '')
            .replace(/,?\s*p\.?\s*\d+(-\d+)?$/i, '')
            .trim()
        
        // Check if it's an Agency Guideline and return the corresponding PDF path
        for (const { pattern, path } of agencyGuidelinesMappings) {
            if (pattern.test(fileNameForCheck)) {
                return path
            }
        }
        
        let bestMatch = null
        let maxScore = 0
        let maxLongestKeyword = 0
        
        // Normalize input for comparison
        const normalizedInput = fileName.toLowerCase().replace(/[^a-z0-9]/g, '')
        
        // Extract document name without lender prefix for better matching
        // Support formats: "Lender_DocumentName.pdf" or "Lender - DocumentName.pdf"
        let documentNameOnly = fileName
        const underscoreIndex = documentNameOnly.indexOf('_')
        if (underscoreIndex !== -1) {
            documentNameOnly = documentNameOnly.substring(underscoreIndex + 1).trim()
        } else if (documentNameOnly.includes(' - ')) {
            const dashIndex = documentNameOnly.indexOf(' - ')
            documentNameOnly = documentNameOnly.substring(dashIndex + 3).trim()
        }
        const normalizedDocumentNameOnly = documentNameOnly.toLowerCase().replace(/[^a-z0-9]/g, '')
        
        // Use document name only for matching if it's different from the full filename
        // This ensures we match the actual document name, not the lender prefix
        const fileNameForMatching = (normalizedDocumentNameOnly && normalizedDocumentNameOnly !== normalizedInput) 
            ? documentNameOnly 
            : fileName
        
        for (const [lenderId, lender] of Object.entries(lenderData)) {
            for (const pdf of lender.pdfs) {
                const pdfDisplayName = pdf.displayName || ''
                const normalizedPdfName = pdfDisplayName.toLowerCase().replace(/[^a-z0-9]/g, '')
                
                // Priority 1: Exact match with document name only (without lender prefix) - highest priority
                // This handles cases like "AcraLending_WS-Ratematrix-DSCR-Summary.pdf" -> "WS-Ratematrix-DSCR-Summary.pdf"
                // Use strict equality to avoid substring false positives
                if (normalizedDocumentNameOnly && normalizedDocumentNameOnly !== normalizedInput) {
                    if (normalizedDocumentNameOnly === normalizedPdfName) {
                        // Exact match with document name only - return immediately
                        return pdf.path
                    }
                }
                
                // Priority 2: Exact match with full filename (only if document name match failed)
                // Use strict equality to avoid substring false positives
                if (normalizedInput === normalizedPdfName) {
                    // Exact match - return immediately
                    return pdf.path
                }
                
                // Priority 2: Calculate weighted score based on keyword matches
                // Longer keywords get higher weight (more specific = better match)
                // Check both original fileName (for lender keywords) and fileNameForMatching (for document keywords)
                let score = 0
                let matchCount = 0
                let longestMatchedKeyword = 0
                for (const keyword of pdf.keywords || []) {
                    // Check if keyword matches in either the original fileName or the document name only
                    // This handles cases where lender name is in the keyword but not in the document name
                    if (fileName.includes(keyword) || fileNameForMatching.includes(keyword)) {
                        matchCount++
                        // Weight by keyword length - longer keywords are more specific
                        score += keyword.length
                        // Track the longest matched keyword for tie-breaking
                        if (keyword.length > longestMatchedKeyword) {
                            longestMatchedKeyword = keyword.length
                        }
                    }
                }
                
                // Require at least 2 keyword matches
                // When scores are equal, prefer the match with the longest keyword (more specific)
                if (matchCount >= 2 && (score > maxScore || (score === maxScore && longestMatchedKeyword > maxLongestKeyword))) {
                    maxScore = score
                    maxLongestKeyword = longestMatchedKeyword
                    bestMatch = pdf.path
                }
            }
        }
        
        return bestMatch
    };

    // Helper function to parse lender name and document name
    // Note: document always keeps the full original filename for display in popover
    const parseLenderAndDocument = (documentName) => {
        if (!documentName) return { lender: null, document: null }
        
        // Priority 0: Check for Agency Guidelines (FHA, VA, USDA, Fannie Mae, Freddie Mac)
        const agencyMappings = [
            { pattern: /^FHA\s*(Guidelines?|Handbook)?/i, lender: 'FHA' },
            { pattern: /^VA\s*(Guidelines?|Handbook|Lender'?s?\s*Handbook)?/i, lender: 'VA' },
            { pattern: /^USDA\s*(Guidelines?|Loan)?/i, lender: 'USDA' },
            { pattern: /^Fannie\s*Mae/i, lender: 'Fannie Mae' },
            { pattern: /^Freddie\s*Mac/i, lender: 'Freddie Mac' },
        ]
        
        const docNameWithoutPdf = documentName.replace(/\.pdf$/i, '').trim()
        for (const { pattern, lender } of agencyMappings) {
            if (pattern.test(docNameWithoutPdf)) {
                return { lender, document: documentName.trim() }
            }
        }
        
        // Priority 1: Check for underscore separator (filename format: Lender_DocumentName.pdf)
        const underscoreIndex = documentName.indexOf('_')
        if (underscoreIndex !== -1) {
            const lenderPart = documentName.substring(0, underscoreIndex).trim()
            
            // Verify lenderPart matches a known lender in lenderInfo
            for (const [lenderId, lender] of Object.entries(lenderInfo)) {
                // Check if lenderPart matches lenderId or displayName (case-insensitive)
                if (lenderId.toLowerCase() === lenderPart.toLowerCase() ||
                    lender.displayName?.toLowerCase() === lenderPart.toLowerCase()) {
                    return { 
                        lender: lender.displayName || lenderPart, 
                        document: documentName.trim() // Keep full filename
                    }
                }
            }
            
            // If lenderPart looks like a valid lender name (not a common keyword), use it
            // Common keywords that shouldn't be treated as lender names
            const commonKeywords = ['FHA', 'VA', 'USDA', 'Conventional', 'Jumbo', 'ARM', 'Fixed']
            if (!commonKeywords.includes(lenderPart.toUpperCase())) {
                return { 
                    lender: lenderPart, 
                    document: documentName.trim() // Keep full filename
                }
            }
        }
        
        // Priority 2: Split by " - " (space-dash-space) to separate lender and document
        const dashIndex = documentName.indexOf(' - ')
        if (dashIndex !== -1) {
            const lender = documentName.substring(0, dashIndex).trim()
            const document = documentName.substring(dashIndex + 3).trim()
            return { lender, document }
        }
        
        // Priority 3: Keyword matching (fallback)
        let bestMatch = null
        let maxMatches = 0
        
        // Use the loaded lender info
        for (const [lenderId, lender] of Object.entries(lenderInfo)) {
            for (const pdf of lender.pdfs) {
                let matches = 0
                for (const keyword of pdf.keywords) {
                    if (documentName.includes(keyword)) {
                        matches++
                    }
                }
                if (matches > maxMatches && matches >= 2) {
                    maxMatches = matches
                    bestMatch = lender.displayName
                }
            }
        }
        
        if (bestMatch) {
            return { 
                lender: bestMatch, 
                document: documentName.trim() 
            }
        }
        
        return { lender: null, document: documentName.trim() }
    }

    // Handle external link clicks with confirmation dialog
    const handleLinkClick = useCallback((event, href) => {
        event.preventDefault()

        // Check if it's an external link (starts with http/https)
        if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
            Modal.confirm({
                title: 'External Link Confirmation',
                icon: <ExclamationCircleOutlined />,
                content: (
                    <div>
                        <p>You are about to navigate to an external link:</p>
                        <p
                            style={{
                                wordBreak: 'break-all',
                                backgroundColor: '#f5f5f5',
                                padding: '8px',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                            }}
                        >
                            {href}
                        </p>
                        <p>Click OK to open this link in a new tab.</p>
                    </div>
                ),
                okText: 'OK',
                cancelText: 'Cancel',
                onOk() {
                    window.open(href, '_blank', 'noopener,noreferrer')
                },
                onCancel() {
                    // Do nothing when cancelled
                },
            })
        } else {
            // For relative links or other protocols, open directly
            window.open(href, '_blank', 'noopener,noreferrer')
        }
    }, [])

    const preprocessMarkdown = useCallback((markdown) => {
        // Remove "Referenced context:" lines that contain reference links
        // Handle multiple formats including markdown formatting (**, *, etc.)

        // Remove all variations of "Referenced context:" with array content (including multiline)
        // This handles: Referenced context: [...], **Referenced context**: [...], ### Referenced context: [...]
        // The pattern matches "Referenced Context:" (case-insensitive) followed by multiple [\"...\"][\"...\"] patterns
        
        // Remove complete lines with Referenced Context (most common case)
        // Matches: **Referenced context:** [\"...\"][\"...\"]...
        markdown = markdown.replace(/^#{0,6}\s*\*{0,2}referenced\s+context\*{0,2}:\s*(?:\[\\?"[^\]]*\\?"\])+\s*$/gmi, '')
        
        // Remove inline Referenced Context patterns (anywhere in text)
        markdown = markdown.replace(/\*{0,2}referenced\s+context\*{0,2}:\s*(?:\[\\?"[^\]]*\\?"\])+/gi, '')
        
        // Catch-all: Remove any remaining lines that contain "Referenced context:"
        markdown = markdown.replace(/^.*referenced\s+context.*$/gmi, '')
        
        // Clean up any orphaned asterisks (markdown formatting) that may be left after removing content
        // Remove lines that only contain asterisks and whitespace (but not part of valid markdown)
        markdown = markdown.replace(/^\s*\*{1,2}\s*$/gm, '')
        // Remove trailing asterisks only if they appear alone at the end of a line (not part of markdown formatting)
        // Only remove if there's whitespace before the asterisks (indicating they're orphaned)
        markdown = markdown.replace(/\s+\*{1,2}\s*$/gm, '')

        // Fix reference format: Convert [filename, p.1, p.2, p.3] to [filename, p.1][filename, p.2][filename, p.3]
        // Match pattern: [filename.pdf, p.X, p.Y, p.Z, ...] (supports both p.178 and p. 178 formats)
        markdown = markdown.replace(/\[([^,\[\]]+\.pdf),\s*(p\.\s*\d+(?:\s*,\s*p\.\s*\d+)+)\]/g, (match, filename, pages) => {
            // Split the pages by comma
            const pageList = pages.split(/\s*,\s*/).map(p => p.trim());
            // Create individual references for each page
            return pageList.map(page => `[${filename}, ${page}]`).join('');
        })

        // Remove specific unicode characters
        markdown = markdown.replace(/\u3010.*?\u3011/g, '')

        // Add a newline after the colon to ensure line breaks
        markdown = markdown.replace(/:\s*$/gm, ':\n')

        // Add a newline before `a)`, `b)`, `c)`
        markdown = markdown.replace(/(\n\s*)([a-z]\))\s+/g, '\n$1$2 ')

        // Convert h3 to h6 and ensure bold
        markdown = markdown.replace(
            /(^|\n)###\s+(.*?)($|\n)/g,
            (match, start, p1, end) => {
                p1 = p1.replace(/\*\*/g, '')
                return `${start}<span style="font-size: 16px; font-weight: bold;">${p1.trim()}</span>${end}`
            }
        )

        // Replace reference links with numbers (e.g. [1, 2, 3])
        markdown = markdown.replace(
            /\[([1-9]\d*(?:\s*,\s*[1-9]\d*)*)\]/g,
            (match, numbers) => {
                // Only filter refs if we have valid locations
                if (isNonEmptyQuoteLocations(quoteLocations)) {
                    const refs = numbers.split(',').map(n => parseInt(n.trim()));
                    return refs.length > 0 ? `<reference-link refs="${refs.join(',')}" />` : '';
                }

                // If no valid locations, return empty string
                return ''
            }
        )

        // First handle the new quoted format where everything is inside quotes: ["Document Name, p.N"]
        markdown = markdown.replace(
            /(?:\["([^"]+,\s*p\.\d+(?:-\d+)?)"\]\s*)+/g,
            (fullMatch) => {
                // Extract all individual quoted references with internal commas
                const individualRefs = fullMatch.match(/\["([^"]+,\s*p\.\d+(?:-\d+)?)"\]/g) || [];
                
                if (individualRefs.length === 1) {
                    // Single quoted reference with internal comma
                    const singleMatch = individualRefs[0].match(/\["([^"]+),\s*p\.(\d+(?:-\d+)?)"\]/);
                    if (singleMatch) {
                        const [, docName, pageNum] = singleMatch;
                        return `<reference-link refs="${docName.trim()}, p.${pageNum}" />`;
                    }
                } else if (individualRefs.length > 1) {
                    // Multiple quoted references with internal commas
                    const refsData = individualRefs.map(match => {
                        const singleMatch = match.match(/\["([^"]+),\s*p\.(\d+(?:-\d+)?)"\]/);
                        if (singleMatch) {
                            const [, docName, pageNum] = singleMatch;
                            return `${docName.trim()}, p.${pageNum}`;
                        }
                        return null;
                    }).filter(Boolean);
                    
                    return `<multiple-reference-links refs="${refsData.join('|||')}" />`;
                }
                
                return fullMatch;
            }
        )

        // Replace document reference links with custom tag - supports both quoted and unquoted formats
        // Handles: [Document, p.N], ["Document", p.N], and consecutive references like [Doc1, p.10][Doc2, p.8] or ["Doc1", p.10]["Doc2", p.8]
        // Supports both p.178 and p. 178 formats (with or without space after p.)
        // IMPORTANT: Escape | as &#124; in refs attribute to avoid Markdown table parsing conflicts
        markdown = markdown.replace(
            /(?:\[(?:"([^"]+)"|(.+?)),\s*p\.\s*(\d+(?:-\d+)?)\])+/g,
            (fullMatch) => {
                // Extract all individual references from the consecutive pattern (both quoted and unquoted)
                const individualRefs = fullMatch.match(/\[(?:"([^"]+)"|(.+?)),\s*p\.\s*(\d+(?:-\d+)?)\]/g) || [];
                
                if (individualRefs.length === 1) {
                    // Single reference - use the original format
                    const singleMatch = individualRefs[0].match(/\[(?:"([^"]+)"|(.+)),\s*p\.\s*(\d+(?:-\d+)?)\]/);
                    if (singleMatch) {
                        const [, quotedDocName, unquotedDocName, pageNum] = singleMatch;
                        const docName = quotedDocName || unquotedDocName;
                        // Escape | character as &#124; to prevent Markdown table parsing conflicts
                        const escapedDocName = docName.trim().replace(/\|/g, '&#124;');
                        return `<reference-link refs="${escapedDocName}, p.${pageNum}" />`;
                    }
                } else if (individualRefs.length > 1) {
                    // Multiple references - use ||| separator (matches renderMultipleScenarioReferences approach)
                    const refsData = individualRefs.map(match => {
                        const singleMatch = match.match(/\[(?:"([^"]+)"|(.+)),\s*p\.\s*(\d+(?:-\d+)?)\]/);
                        if (singleMatch) {
                            const [, quotedDocName, unquotedDocName, pageNum] = singleMatch;
                            const docName = quotedDocName || unquotedDocName;
                            // Escape | character as &#124; to prevent Markdown table parsing conflicts
                            const escapedDocName = docName.trim().replace(/\|/g, '&#124;');
                            return `${escapedDocName}, p.${pageNum}`;
                        }
                        return null;
                    }).filter(Boolean);
                    
                    return `<multiple-reference-links refs="${refsData.join('|||')}" />`;
                }
                
                return fullMatch;
            }
        )
        
        // Handle the new format: [p.N, M-P] (supports both p.178 and p. 178 formats)
        markdown = markdown.replace(
            /\[p\.\s*(\d+),\s*(\d+(?:-\d+)?)\]/g,
            (match, pageNum, range) => {
                return `<reference-link refs="p.${pageNum}, ${range}" />`;
            }
        )
        
        // Handle the new format: [p.N, p.M] (supports both p.178 and p. 178 formats)
        markdown = markdown.replace(
            /\[p\.\s*(\d+),\s*p\.\s*(\d+)\]/g,
            (match, pageNum1, pageNum2) => {
                return `<reference-link refs="p.${pageNum1}, p.${pageNum2}" />`;
            }
        )

        // Handle the format: [Document Name.pdf, p.N, p.M] (supports both p.178 and p. 178 formats)
        markdown = markdown.replace(
            /\[(.+\.pdf),\s*p\.\s*(\d+),\s*p\.\s*(\d+)\]/g,
            (fullMatch, docName, pageNum1, pageNum2) => {
                // Use the first page number as the primary target
                return `<reference-link refs="${docName.trim()}, p.${pageNum1}" />`;
            }
        )

        return markdown
    }, [quoteLocations])

    const renderNumberReference = (refsString) => {
        const refs = refsString.split(',').map((ref) => ref.trim())
        const allPages = quoteLocations.map(([page]) => page)
        
        // Map reference numbers to actual page numbers
        const refPageNumber = (ref) => {
            if (pageNumber && pageNumber[ref] !== undefined) {
                const index = pageNumber[ref]
                if (index >= 0 && index < allPages.length) {
                    return { index, page: allPages[index] }
                }
                return { index, page: ref }
            } else {
                // Fallback to original logic
                const refsIndex = parseRefsIndex(answer)
                const index = refsIndex.indexOf(parseInt(ref))
                if (index >= 0 && index < allPages.length) {
                    return { index, page: allPages[index] }
                }
                return { index, page: ref }
            }
        }

        // Get page number that the reference link points to
        const handleClick = (ref) => {
            const { index, page } = refPageNumber(ref)
            if (page) {
                referenceFunc(page, quoteLocations, index)
            }
        }

        return (
            <span className="reference-link">
                {refs.map((ref, idx) => (
                    <span
                        key={idx}
                        className="page-number"
                        onClick={() => handleClick(ref)}
                    >
                        {refPageNumber(ref).page}
                    </span>
                ))}
            </span>
        )
    }


    // Helper function to get lender icon using partial matching
    const getLenderIcon = (lenderName) => {
        if (!lenderName) return '📄';
        
        // Try exact match first
        let iconSrc = ICON[lenderName];
        
        // If no exact match, try partial matching
        if (!iconSrc) {
            const lenderKeys = Object.keys(ICON);
            const matchedKey = lenderKeys.find(key => 
                lenderName.toLowerCase().includes(key.toLowerCase()) ||
                key.toLowerCase().includes(lenderName.toLowerCase())
            );
            if (matchedKey) {
                iconSrc = ICON[matchedKey];
            }
        }
        
        if (iconSrc) {
            return (
                <img 
                    src={iconSrc} 
                    alt={lenderName}
                    style={{ 
                        width: '24px', 
                        height: '24px', 
                        objectFit: 'contain' 
                    }} 
                />
            );
        }
        
        // Fallback to generic document icon if no specific icon found
        return '📄';
    }

    const renderScenarioReference = (refsString) => {
        // Decode HTML entity &#124; back to | character
        const decodedRefsString = refsString.replace(/&#124;/g, '|');
        

        // Parse PDF filename and page from the reference string
        // Format: "PDF Name.pdf, p.N" or "Lender - Program, p.N" or "Document, p.N-M" or "p.N, M-P" or "p.N, p.M"
        // New format: "Document Name.pdf, p.N, p.M" (extract first page)
        
        // Try the new multi-page format first (most specific)
        let match = decodedRefsString.match(/^(.+\.pdf),\s*p\.(\d+),\s*p\.(\d+)$/)
        if (!match) {
            // Try other patterns
            match = decodedRefsString.match(/^(.+?),\s*p\.(\d+(?:-\d+)?)$/) || 
                   decodedRefsString.match(/^p\.(\d+),\s*(\d+(?:-\d+)?)$/) ||
                   decodedRefsString.match(/^p\.(\d+),\s*p\.(\d+)$/)
        }
        if (match) {
            let documentName, pageNum
            
            // Handle different match patterns
            if (match[1] && match[2] && match[3]) {
                // Format: "Document Name.pdf, p.N, p.M" - use first page
                documentName = match[1]
                pageNum = match[2]
            } else if (match[1] && match[2] && !match[1].includes('p.')) {
                // Format: "Document, p.N" or "Document, p.N-M"
                documentName = match[1]
                pageNum = match[2]
            } else if (match[1] && match[2] && match[1].includes('p.')) {
                // Format: "p.N, p.M" - page number is in match[1], second page is in match[2]
                documentName = null // No document name in this format
                pageNum = match[1].replace('p.', '') // Extract just the number
            } else if (match[1] && match[2] === undefined) {
                // Format: "p.N, M-P" - page number is in match[1], range is in match[2]
                documentName = null // No document name in this format
                pageNum = match[1]
            }
            
            // Validate that the PDF exists in LENDER_INFO before rendering
            if (documentName) {
                let pdfFileName = documentName.trim()
                if (!pdfFileName.includes('.pdf')) {
                    pdfFileName = pdfFileName + '.pdf'
                }
                
                const pdfPath = findPdfByKeywordsSync(pdfFileName, lenderInfo);
                // Allow Agency Guidelines (AGENCY_GUIDELINE) and valid lender PDFs
                // Only hide if pdfPath is null (not found and not an agency guideline)
                if (!pdfPath) {
                    // PDF not found in LENDER_INFO - hide the invalid reference
                    return null
                }
            }
            
            const handleDocClick = (e) => {
                // Prevent event bubbling
                if (e) {
                    e.stopPropagation();
                    e.preventDefault();
                }
                
                // Extract the first page number for jumping (handle ranges like "23-24")
                let targetPage = parseInt(pageNum.split('-')[0], 10)
                
                // Map document name to actual PDF filename
                let pdfFileName = documentName ? documentName.trim() : null
                
                // Pass document name directly to Chat.js for keyword-based matching
                // The Chat.js findPdfByKeywords function will handle finding the right PDF file
                // (including removing lender prefix if needed)
                if (pdfFileName && !pdfFileName.includes('.pdf')) {
                    pdfFileName = documentName.trim() + '.pdf'
                }
                
                // For dynamic PDF loading, we need to pass both the PDF name and page
                if (referenceFunc) {
                    if (pdfFileName) {
                        // Has document name - use dynamic loading
                        referenceFunc(targetPage, quoteLocations, 0, {
                            pdfFileName: pdfFileName,
                            dynamicLoad: true
                        })
                    } else {
                        // No document name - just jump to page in current document
                        referenceFunc(targetPage, quoteLocations, 0)
                    }
                } else {
                    console.warn('referenceFunc is not available')
                }
            }

            // Parse lender and document names
            const { lender, document } = parseLenderAndDocument(documentName)
            
            // If we have a lender name, show it with popover
            if (lender && document) {
                const popoverContent = (
                    <div style={{ maxWidth: 246 }}>
                        {/* First row: Lender icon and name */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: 10,
                            gap: 8
                        }}>
                            <div>{getLenderIcon(lender)}</div>
                            <span>{lender}</span>
                        </div>
                        
                        {/* Second row: Document name (clickable) */}
                        <div 
                            style={{ 
                                fontWeight: 600,
                                color: '#0F172A',
                                cursor: 'pointer',
                                wordBreak: 'break-word'
                            }}
                            onClick={handleDocClick}
                        >
                            {document}, p.{pageNum}
                        </div>
                    </div>
                )

                if(!isMobile && isGenerating && !firstReference.current){
                    firstReference.current = true;
                     // 🐮的 👍
                     handleDocClick();
                     handleDocClick();   
                }

                return (
                    <span className="reference-link">
                        <Popover
                            overlayClassName='reference-popover'
                            arrow={false}
                            content={popoverContent} 
                            trigger="hover"
                            placement="bottom"
                        >
                            <span 
                                onClick={handleDocClick} 
                                onMouseDown={handleDocClick}
                                className="page-number lender-reference" 
                                style={{ cursor: 'pointer' }}
                            >
                                {lender}
                            </span>
                        </Popover>
                    </span>
                )
            }

            // Fallback to original behavior if no lender name found
            return (
                <span className="reference-link">
                    <span className="page-number" onClick={handleDocClick}>
                        {decodedRefsString}
                    </span>
                </span>
            )
        }

        // Fallback to original logic if parsing fails
        let actualPage = decodedRefsString
        let actualIndex = 0

        if (quoteLocations) {
            const allPages = quoteLocations.map(([page]) => page)
            if (pageNumber && pageNumber[decodedRefsString] !== undefined) {
                const index = pageNumber[decodedRefsString]
                if (index >= 0 && index < allPages.length) {
                    actualPage = allPages[index]
                    actualIndex = index
                }
            }
        }


        const handleDocClick = () => {
            referenceFunc(actualPage, quoteLocations, actualIndex)
        }

        return (
            <span className="reference-link">
                <span className="page-number" onClick={handleDocClick}>
                    {decodedRefsString}
                </span>
            </span>
        )
    }

    const renderMultipleScenarioReferences = (refsString) => {
        // Decode HTML entity &#124; back to | character before splitting
        const decodedRefsString = refsString.replace(/&#124;/g, '|');
        // Split multiple references by '|||' (using triple pipe to avoid conflicts with document names containing '|')
        const refs = decodedRefsString.split('|||').map(ref => ref.trim());
        
        // Validate and filter out invalid references
        const validRefs = refs.filter(ref => {
            const match = ref.match(/^(.+?),\s*p\.(\d+(?:-\d+)?)$/);
            if (match) {
                const [, documentName] = match;
                let pdfFileName = documentName.trim();
                if (!pdfFileName.includes('.pdf')) {
                    pdfFileName = pdfFileName + '.pdf';
                }
                
                const pdfPath = findPdfByKeywordsSync(pdfFileName, lenderInfo);
                return !!pdfPath; // Convert to boolean
            }
            return false;
        });
        
        // If no valid references, hide the entire multiple-reference-links
        if (validRefs.length === 0) {
            return null;
        }
        
        return (
            <span className="reference-link multiple-reference-links">
                {validRefs.map((ref, idx) => {
                    // Parse each reference
                    const match = ref.match(/^(.+?),\s*p\.(\d+(?:-\d+)?)$/);
                    if (match) {
                        const [, documentName, pageNum] = match;
                        
                        const handleDocClick = () => {
                            // Extract the first page number for jumping (handle ranges like "23-24")
                            let targetPage = parseInt(pageNum.split('-')[0], 10);
                            
                            // Map document name to actual PDF filename
                            let pdfFileName = documentName ? documentName.trim() : null;
                            
                            // The findPdfByKeywords function will handle finding the right PDF file
                            // (including removing lender prefix if needed)
                            if (pdfFileName && !pdfFileName.includes('.pdf')) {
                                pdfFileName = documentName.trim() + '.pdf';
                            }
                                       
                            if (referenceFunc) {
                                if (pdfFileName) {
                                    // Has document name - use dynamic loading
                                    referenceFunc(targetPage, quoteLocations, 0, {
                                        pdfFileName: pdfFileName,
                                        dynamicLoad: true
                                    });
                                } else {
                                    // No document name - just jump to page in current document
                                    referenceFunc(targetPage, quoteLocations, 0);
                                }
                            }
                        };
                        
                        // Parse lender and document names for multiple references too
                        const { lender, document } = parseLenderAndDocument(documentName);
                        
                        // If we have a lender name, show it with popover
                        if (lender && document) {
                            const popoverContent = (
                                <div style={{ maxWidth: 246 }}>
                                    {/* First row: Lender icon and name */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        marginBottom: 10,
                                        gap: 8
                                    }}>
                                        <div>{getLenderIcon(lender)}</div>
                                        <span>{lender}</span>
                                    </div>
                                    
                                    {/* Second row: Document name (clickable) */}
                                    <div 
                                        style={{ 
                                            fontWeight: 600,
                                            color: '#0F172A',
                                            cursor: 'pointer',
                                            wordBreak: 'break-word'
                                        }}
                                        onClick={handleDocClick}
                                    >
                                        {document}, p.{pageNum}
                                    </div>
                                </div>
                            );

                            // Always render with Popover, but disable trigger during generation
                            return (
                                <Popover
                                    key={idx}
                                    overlayClassName='reference-popover'
                                    arrow={false}
                                    content={popoverContent} 
                                    trigger="hover"
                                    placement="bottom"
                                >
                                    <span 
                                        onClick={handleDocClick}
                                        onMouseDown={handleDocClick}
                                        className="page-number lender-reference" 
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {lender}
                                    </span>
                                </Popover>
                            );
                        }
                        
                        // Fallback to original behavior
                        return (
                            <span
                                key={idx}
                                className="page-number"
                                onClick={handleDocClick}
                            >
                                {ref}
                            </span>
                        );
                    }
                    return null;
                }).filter(Boolean)}
            </span>
        );
    };

    const components = useMemo(() => ({
        // Custom link component to handle external links with confirmation
        a: ({ node, href, children, ...props }) => {
            return (
                <a
                    href={href}
                    {...props}
                    onClick={(event) => handleLinkClick(event, href)}
                    style={{
                        color: '#1890ff',
                        textDecoration: 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline'
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none'
                    }}
                >
                    {children}
                </a>
            )
        },
        // Custom styling for ordered lists
        ol: ({ node, children, ...props }) => {
            return (
                <ol
                    {...props}
                    className='markdown-ol'
                >
                    {children}
                </ol>
            )
        },
        // Custom styling for unordered lists
        ul: ({ node, children, ...props }) => {
            return (
                <ul
                    {...props}
                    className='markdown-ul'
                >
                    {children}
                </ul>
            )
        },
        // Custom styling for list items
        li: ({ node, children, ...props }) => {
            return (
                <li
                    {...props}
                    className='markdown-li'
                >
                    {children}
                </li>
            )
        },
        // Custom styling for strong/bold text
        strong: ({ node, children, ...props }) => {
            return (
                <strong
                    {...props}
                    className='markdown-strong'
                >
                    {children}
                </strong>
            )
        },
        // Custom styling for paragraphs
        p: ({ node, children, ...props }) => {
            return (
                <p
                    {...props}
                    className='markdown-p'
                >
                    {children}
                </p>
            )
        },
        // Custom styling for h1
        h1: ({ node, children, ...props }) => {
            return (
                <h1
                    {...props}
                    className='markdown-h1'
                >
                    {children}
                </h1>
            )
        },
        // Custom styling for h2
        h2: ({ node, children, ...props }) => {
            return (
                <h2
                    {...props}
                    className='markdown-h2'
                >
                    {children}
                </h2>
            )
        },
        // Custom styling for h3
        h3: ({ node, children, ...props }) => {
            return (
                <h3
                    {...props}
                    className='markdown-h3'
                >
                    {children}
                </h3>
            )
        },
        // Custom styling for h4
        h4: ({ node, children, ...props }) => {
            return (
                <h4
                    {...props}
                    className='markdown-h4'
                >
                    {children}
                </h4>
            )
        },
        // Custom styling for table
        table: ({ node, children, ...props }) => {
            return (
                <table
                    {...props}
                    className='markdown-table'
                >
                    {children}
                </table>
            )
        },
        // Custom styling for table header
        thead: ({ node, children, ...props }) => {
            return (
                <thead
                    {...props}
                    className='markdown-thead'
                >
                    {children}
                </thead>
            )
        },
        // Custom styling for table body
        tbody: ({ node, children, ...props }) => {
            return (
                <tbody
                    {...props}
                    className='markdown-tbody'
                >
                    {children}
                </tbody>
            )
        },
        // Custom styling for table row
        tr: ({ node, children, ...props }) => {
            return (
                <tr
                    {...props}
                    className='markdown-tr'
                >
                    {children}
                </tr>
            )
        },
        // Custom styling for table header cell
        th: ({ node, children, ...props }) => {
            return (
                <th
                    {...props}
                    className='markdown-th'
                >
                    {children}
                </th>
            )
        },
        // Custom styling for table data cell
        td: ({ node, children, ...props }) => {
            return (
                <td
                    {...props}
                    className='markdown-td'
                >
                    {children}
                </td>
            )
        },
        'reference-link': ({ node }) => {
            const refsString = node.properties?.refs
            // Check if this is a document reference (contains "p.")
            if (refsString.includes('p.')) {
                const result = renderScenarioReference(refsString)
                return result
            } else {
                const result = renderNumberReference(refsString)
                return result
            }
        },
        'multiple-reference-links': ({ node }) => {
            const refsString = node.properties?.refs
            const result = renderMultipleScenarioReferences(refsString)
            return result
        },
    }), [isGenerating, lenderInfo])

    const markdownClassName = `markdown-body ${isGenerating ? 'generating' : ''}`
    
    // Memoize processed markdown to reduce re-renders
    const processedMarkdown = useMemo(() => {
        return preprocessMarkdown(answer)
    }, [answer, preprocessMarkdown])
    
    return (
        <ReactMarkdown
            className={markdownClassName}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
            components={components}
        >
            {processedMarkdown}
        </ReactMarkdown>
    )
}

export default MarkdownRenderer
